/**
 * MfaEnrollmentGate — mandatory-MFA rollout enforcement.
 * =====================================================
 * Corvana requires every (non-demo) account to enrol an MFA method. On first
 * login the backend stamps a 7-day grace window (`mfa_grace_until`). This gate:
 *   • During grace  → shows a dismissible countdown nudge (warning signal), and
 *                     a one-tap "Set up now" that opens the full setup inline.
 *   • After grace   → hard-blocks the page behind an enrolment screen that lets
 *                     the user add a passkey / authenticator / email code RIGHT
 *                     THERE (no bounce to a settings URL) and confirms success.
 *   • Enrolled/demo  → fully transparent.
 *
 * Applied in two places so EVERY authenticated area is covered, not just /app:
 *   • MainLayout wraps /app content (nudge sits inside the app chrome).
 *   • ProtectedRoute wraps every other protected tree (CRM, partner, tech).
 *
 * Enrolment state comes live from GET /api/auth/2fa/status (`enrollment`), so it
 * clears the instant a method is added. Fails OPEN on any network/API error so a
 * transient blip never locks a user out.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Fingerprint, ShieldCheck, KeyRound, Mail, X, Copy, Check, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';
import { registerPasskey } from '@/utils/passkey';
import { toast } from '@/utils/safeToast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Per-user session cache: holds the user id for whom the gate is already
// cleared (enrolled / demo-exempt / fail-open). Keyed by id so one user's
// cleared state can never leak to the next login in the same tab.
let _mfaClearFor = null;

function daysLeft(graceUntil) {
  if (!graceUntil) return null;
  try {
    const ms = new Date(graceUntil).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  } catch (_) { return null; }
}

// ───────────────────────── Inline setup panel ─────────────────────────
// Lets the user actually finish MFA setup wherever they are — no navigation
// away to /app/security. Calls the same /api/auth/2fa/* endpoints the Security
// page uses and reports success back via onEnrolled().
function InlineEnrollPanel({ onEnrolled }) {
  const [step, setStep] = React.useState('choose');   // choose | authenticator | recovery
  const [busy, setBusy] = React.useState(false);
  const [setupData, setSetupData] = React.useState(null);
  const [code, setCode] = React.useState('');
  const [recovery, setRecovery] = React.useState([]);

  const addPasskey = async () => {
    setBusy(true);
    const res = await registerPasskey();
    setBusy(false);
    if (res.success) { toast.success('Passkey added — your account is secured'); onEnrolled(); }
    else { toast.error(res.error || 'Could not add passkey'); }
  };

  const startAuthenticator = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${API_URL}/api/auth/2fa/setup`, { method: 'POST' });
      if (r.ok) { setSetupData(await r.json()); setStep('authenticator'); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Could not start setup'); }
    } catch (_) { toast.error('Could not start setup'); }
    setBusy(false);
  };

  const finish = (codes) => {
    if (Array.isArray(codes) && codes.length) { setRecovery(codes); setStep('recovery'); }
    else { onEnrolled(); }
  };

  const verifyAuthenticator = async () => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code from your app'); return; }
    setBusy(true);
    try {
      const r = await authFetch(`${API_URL}/api/auth/2fa/verify-setup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
      });
      if (r.ok) { const d = await r.json(); toast.success('Two-step verification enabled'); finish(d.recovery_codes); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Invalid code'); }
    } catch (_) { toast.error('Could not verify code'); }
    setBusy(false);
  };

  const enableEmail = async () => {
    setBusy(true);
    try {
      const r = await authFetch(`${API_URL}/api/auth/2fa/email/enable`, { method: 'POST' });
      if (r.ok) { const d = await r.json(); toast.success('Email verification codes enabled'); finish(d.recovery_codes); }
      else { toast.error('Could not enable email codes'); }
    } catch (_) { toast.error('Could not enable email codes'); }
    setBusy(false);
  };

  if (step === 'recovery') {
    return (
      <div className="space-y-4" data-testid="mfa-recovery-codes">
        <p className="text-sm text-slate-600">
          Save these one-time backup codes somewhere safe — each lets you sign in once if you
          lose your device.
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 border border-slate-200 p-3 font-mono text-sm">
          {recovery.map((c) => <span key={c} className="text-slate-800">{c}</span>)}
        </div>
        <button type="button"
          onClick={() => { try { navigator.clipboard?.writeText(recovery.join('\n')); } catch (_) {} }}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900">
          <Copy className="w-3.5 h-3.5" /> Copy codes
        </button>
        <button type="button" onClick={onEnrolled} data-testid="mfa-recovery-done"
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-3 rounded-xl transition-colors">
          <Check className="w-4 h-4" /> I've saved these — continue
        </button>
      </div>
    );
  }

  if (step === 'authenticator' && setupData) {
    return (
      <div className="space-y-4" data-testid="mfa-authenticator-setup">
        <button type="button" onClick={() => { setStep('choose'); setCode(''); }}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <p className="text-sm text-slate-600">
          Scan this with an authenticator app (Google Authenticator, 1Password, Authy…), then enter the 6-digit code.
        </p>
        {setupData.qr_code && (
          <div className="flex justify-center">
            <img src={setupData.qr_code} alt="Authenticator QR code" className="w-44 h-44 rounded-lg border border-slate-200"
              data-testid="mfa-authenticator-qr" />
          </div>
        )}
        {setupData.secret && (
          <div className="text-center text-xs text-slate-500">
            or enter this key manually: <span className="font-mono text-slate-700">{setupData.secret}</span>
          </div>
        )}
        <input inputMode="numeric" maxLength={6} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000" data-testid="mfa-authenticator-code-input"
          className="w-full text-center tracking-[0.5em] font-mono text-lg border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        <button type="button" onClick={verifyAuthenticator} disabled={busy} data-testid="mfa-authenticator-verify"
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 px-4 py-3 rounded-xl transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Verify & turn on
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="mfa-enroll-choose">
      <button type="button" onClick={addPasskey} disabled={busy} data-testid="mfa-enroll-passkey"
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-60 px-4 py-3 rounded-xl transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
        Add a passkey (Face ID / fingerprint)
      </button>
      <button type="button" onClick={startAuthenticator} disabled={busy} data-testid="mfa-enroll-authenticator"
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-60 px-4 py-3 rounded-xl transition-colors">
        <KeyRound className="w-4 h-4" /> Use an authenticator app
      </button>
      <button type="button" onClick={enableEmail} disabled={busy} data-testid="mfa-enroll-email"
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-60 px-4 py-3 rounded-xl transition-colors">
        <Mail className="w-4 h-4" /> Email me a code each time
      </button>
    </div>
  );
}

function NudgeBanner({ graceUntil, onSetup, storageKey }) {
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem(storageKey) === '1'
  );
  if (dismissed) return null;
  const left = daysLeft(graceUntil);
  return (
    <div
      className="mb-5 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="mfa-nudge-banner"
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="p-2 rounded-lg bg-amber-500/15 shrink-0">
          <ShieldCheck className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900">Action needed: set up two-step verification</p>
          <p className="text-xs text-slate-600">
            Corvana will require it to keep your business data safe.
            {left != null && (
              <span className="font-semibold text-amber-700">
                {' '}{left === 0 ? 'Access pauses today until it\u2019s set up.' : `${left} day${left === 1 ? '' : 's'} left before access pauses.`}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={onSetup} data-testid="mfa-nudge-setup"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-2 rounded-lg transition-colors">
          <Fingerprint className="w-3.5 h-3.5" /> Set up now
        </button>
        <button type="button"
          onClick={() => { sessionStorage.setItem(storageKey, '1'); setDismissed(true); }}
          aria-label="Dismiss" className="text-slate-400 hover:text-slate-600 p-1" data-testid="mfa-nudge-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function RequiredScreen({ onEnrolled }) {
  return (
    <div className="py-10 flex items-center justify-center px-4" data-testid="mfa-required-screen">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-4">
            <ShieldCheck className="w-7 h-7 text-indigo-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900">Two-step verification required</h2>
          <p className="text-sm text-slate-600 mt-2">
            Your setup grace period has ended, so Corvana has <span className="font-medium text-slate-800">paused
            access to your data</span> until you add a second sign-in step. That&rsquo;s why your
            dashboards and lists aren&rsquo;t loading. Set one up below — it takes under a minute.
          </p>
        </div>
        <div className="mt-6">
          <InlineEnrollPanel onEnrolled={onEnrolled} />
        </div>
        <p className="text-xs text-slate-400 mt-5 text-center">
          Passkeys are the most secure option and can&apos;t be phished.
        </p>
      </div>
    </div>
  );
}

export function MfaEnrollmentGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const uid = user?.id || null;
  const uidRef = React.useRef(uid);
  React.useEffect(() => { uidRef.current = uid; }, [uid]);
  const [enrollment, setEnrollment] = React.useState(null);
  const [clear, setClear] = React.useState(() => _mfaClearFor != null && _mfaClearFor === uid);
  const [setupOpen, setSetupOpen] = React.useState(false);

  const exempt = !user || user.is_demo === true;

  const markClear = React.useCallback(() => { _mfaClearFor = uid; setClear(true); }, [uid]);

  const fetchStatus = React.useCallback(async () => {
    const forUid = uid;
    try {
      const r = await authFetch(`${API_URL}/api/auth/2fa/status`, {});
      if (uidRef.current !== forUid) return;
      if (r.ok) {
        const d = await r.json();
        const en = d.enrollment || null;
        setEnrollment(en);
        if (!en || en.enrolled) { _mfaClearFor = forUid; setClear(true); }
      } else {
        _mfaClearFor = forUid; setClear(true); // fail-open on non-200
      }
    } catch (_) {
      if (uidRef.current === forUid) { _mfaClearFor = forUid; setClear(true); } // fail-open
    }
  }, [uid]);

  // Called when a method is successfully enrolled anywhere in the gate.
  const onEnrolled = React.useCallback(() => {
    setSetupOpen(false);
    markClear();
    window.dispatchEvent(new CustomEvent('corvana:mfa-refresh'));
    fetchStatus();
  }, [markClear, fetchStatus]);

  React.useEffect(() => {
    if (uid != null && _mfaClearFor === uid) { setClear(true); }
    else { setClear(false); setEnrollment(null); }
  }, [uid]);

  React.useEffect(() => {
    if (exempt) { markClear(); return; }
    if (_mfaClearFor === uid) { setClear(true); return; }
    fetchStatus();
  }, [exempt, uid, location.pathname, fetchStatus, markClear]);

  React.useEffect(() => {
    const h = () => fetchStatus();
    window.addEventListener('corvana:mfa-refresh', h);
    return () => window.removeEventListener('corvana:mfa-refresh', h);
  }, [fetchStatus]);

  if (exempt || clear) return children;
  if (enrollment === null) {
    return (
      <div className="py-24 flex items-center justify-center" data-testid="mfa-gate-loading">
        <Loader2 className="w-7 h-7 animate-spin text-slate-400" />
      </div>
    );
  }
  if (enrollment.enrolled) return children;

  const onSecurity = location.pathname.startsWith('/app/security');
  if (enrollment.grace_expired && !onSecurity) {
    return <RequiredScreen onEnrolled={onEnrolled} />;
  }
  return (
    <>
      {!onSecurity && (
        <NudgeBanner
          graceUntil={enrollment.grace_until}
          storageKey={`corvana_mfa_nudge_dismissed_${uid}`}
          onSetup={() => setSetupOpen(true)}
        />
      )}
      {children}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mfa-setup-dialog">
          <DialogHeader>
            <DialogTitle>Set up two-step verification</DialogTitle>
          </DialogHeader>
          <InlineEnrollPanel onEnrolled={onEnrolled} />
        </DialogContent>
      </Dialog>
    </>
  );
}
